import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  X, Loader2, Phone, MapPin, Mail, Clock, ShoppingBag,
  CheckCircle2, XCircle, AlertCircle, MessageSquare, History, ChevronRight,
  Save, Truck, ChefHat, PackageCheck,
} from "lucide-react";
import { pedidosApi, Pedido, EstadoPedido, EstadoPagoPedido } from "../../lib/api";

function formatUSD(n: string | number): string {
  const num = typeof n === "string" ? Number(n) : n;
  return new Intl.NumberFormat("es-VE", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(num);
}

function formatDateTime(d: string): string {
  return new Date(d).toLocaleString("es-ES", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

const ESTADOS: Array<{ key: EstadoPedido; label: string; color: string; icon: React.ReactNode }> = [
  { key: "nuevo", label: "Nuevo", color: "amber", icon: <AlertCircle className="size-4" /> },
  { key: "confirmado", label: "Confirmado", color: "blue", icon: <CheckCircle2 className="size-4" /> },
  { key: "en_preparacion", label: "En cocina", color: "purple", icon: <ChefHat className="size-4" /> },
  { key: "listo", label: "Listo", color: "indigo", icon: <PackageCheck className="size-4" /> },
  { key: "en_camino", label: "En camino", color: "emerald", icon: <Truck className="size-4" /> },
  { key: "entregado", label: "Entregado", color: "green", icon: <PackageCheck className="size-4" /> },
];

export function PedidoDetalleModal({ pedidoId, onClose }: { pedidoId: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [notas, setNotas] = useState("");
  const [showCancelar, setShowCancelar] = useState(false);
  const [motivoCancelar, setMotivoCancelar] = useState("");

  const pedido = useQuery({
    queryKey: ["pedidos", "get", pedidoId],
    queryFn: () => pedidosApi.get(pedidoId),
  });

  useEffect(() => {
    if (pedido.data?.notasInternas) setNotas(pedido.data.notasInternas);
  }, [pedido.data?.id]);

  const cambiarMut = useMutation({
    mutationFn: (estado: EstadoPedido) => pedidosApi.cambiarEstado(pedidoId, { estado }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pedidos"] });
    },
  });

  const pagoMut = useMutation({
    mutationFn: (estadoPago: EstadoPagoPedido) => pedidosApi.actualizarPago(pedidoId, { estadoPago }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pedidos"] }),
  });

  const notasMut = useMutation({
    mutationFn: () => pedidosApi.actualizarNotas(pedidoId, { notasInternas: notas }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pedidos"] }),
  });

  const cancelarMut = useMutation({
    mutationFn: () => pedidosApi.cambiarEstado(pedidoId, {
      estado: "cancelado", motivoCancelacion: motivoCancelar,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pedidos"] });
      onClose();
    },
  });

  const p: Pedido | undefined = pedido.data;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-200">
          <div className="flex items-center gap-3">
            <ShoppingBag className="size-5 text-brand-600" />
            <div>
              <h2 className="font-display font-bold text-ink-800">
                Pedido {p?.numeroPedido || `#${pedidoId}`}
              </h2>
              {p && <p className="text-xs text-ink-500">{formatDateTime(p.horaSolicitud)} · {p.canalOrigen}</p>}
            </div>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700">
            <X className="size-5" />
          </button>
        </div>

        {pedido.isLoading || !p ? (
          <div className="p-12 text-center text-ink-400">
            <Loader2 className="size-6 animate-spin mx-auto" />
          </div>
        ) : (
          <div className="overflow-y-auto flex-1">
            {/* Timeline de estados */}
            <div className="px-5 py-4 bg-ink-50 border-b border-ink-200">
              <div className="flex items-center gap-1">
                {ESTADOS.filter(e => e.key !== "entregado").map((e, i) => {
                  const idx = ESTADOS.findIndex(x => x.key === p.estado);
                  const active = i <= idx;
                  return (
                    <div key={e.key} className="flex-1 flex items-center gap-1">
                      <div className={`flex-1 h-1.5 rounded-full ${active ? `bg-${e.color}-500` : "bg-ink-200"}`} />
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-2 text-[10px] text-ink-500">
                {ESTADOS.filter(e => e.key !== "entregado").map(e => <span key={e.key}>{e.label}</span>)}
              </div>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Columna izquierda: cliente + entrega */}
              <div className="md:col-span-1 space-y-4">
                <section>
                  <h3 className="text-xs font-bold text-ink-500 uppercase mb-2">Cliente</h3>
                  <div className="text-sm font-medium text-ink-800 mb-1">{p.nombreCliente}</div>
                  {p.telefono && (
                    <a href={`tel:${p.telefono}`} className="block text-xs text-brand-700 hover:underline mb-1">
                      <Phone className="size-3 inline mr-1" />{p.telefono}
                    </a>
                  )}
                  {p.email && (
                    <a href={`mailto:${p.email}`} className="block text-xs text-brand-700 hover:underline">
                      <Mail className="size-3 inline mr-1" />{p.email}
                    </a>
                  )}
                  {p.codCliente && (
                    <div className="text-[10px] text-ink-500 mt-1">CRM #{p.codCliente}</div>
                  )}
                </section>

                <section>
                  <h3 className="text-xs font-bold text-ink-500 uppercase mb-2">Entrega</h3>
                  <div className="text-xs text-ink-700 mb-1">
                    {p.tipoEntrega === "delivery" ? "🚚 Delivery"
                     : p.tipoEntrega === "retiro" ? "🏪 Retiro en local"
                     : "🍽️ Para mesa"}
                  </div>
                  {p.nombreZona && (
                    <div className="text-xs text-brand-600 font-medium mt-1">{p.nombreZona}</div>
                  )}
                  {p.direccionEntrega && (
                    <div className="text-xs text-ink-600 mt-1 flex items-start gap-1">
                      <MapPin className="size-3 mt-0.5 shrink-0" />
                      <span>{p.direccionEntrega}</span>
                    </div>
                  )}
                  {p.referenciaDireccion && (
                    <div className="text-xs text-ink-500 italic mt-1">{p.referenciaDireccion}</div>
                  )}
                </section>

                <section>
                  <h3 className="text-xs font-bold text-ink-500 uppercase mb-2">Pago</h3>
                  <div className="flex items-center gap-2 mb-2">
                    <PagoBadge estado={p.estadoPago} />
                  </div>
                  <div className="flex gap-1.5">
                    {p.estadoPago !== "confirmado" && (
                      <button onClick={() => pagoMut.mutate("confirmado")}
                        disabled={pagoMut.isPending}
                        className="text-[10px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-medium inline-flex items-center gap-1">
                        <CheckCircle2 className="size-3" />Confirmar
                      </button>
                    )}
                    {p.estadoPago !== "rechazado" && (
                      <button onClick={() => pagoMut.mutate("rechazado")}
                        disabled={pagoMut.isPending}
                        className="text-[10px] px-2 py-1 rounded bg-red-100 hover:bg-red-200 text-red-700 font-medium inline-flex items-center gap-1">
                        <XCircle className="size-3" />Rechazar
                      </button>
                    )}
                  </div>
                </section>

                {p.notasCliente && (
                  <section>
                    <h3 className="text-xs font-bold text-ink-500 uppercase mb-1">Notas del cliente</h3>
                    <div className="text-xs text-ink-700 bg-amber-50 border border-amber-200 rounded p-2">
                      {p.notasCliente}
                    </div>
                  </section>
                )}
              </div>

              {/* Columna central: items */}
              <div className="md:col-span-2 space-y-4">
                <section>
                  <h3 className="text-xs font-bold text-ink-500 uppercase mb-2">Items</h3>
                  <div className="divide-y divide-ink-100 border border-ink-200 rounded">
                    {p.lineas?.map((l) => (
                      <div key={l.id} className="px-3 py-2 flex items-start gap-3">
                        {l.fotoUrl ? (
                          <img src={l.fotoUrl} className="size-10 rounded object-cover shrink-0" alt={l.nombreArticulo} />
                        ) : (
                          <div className="size-10 rounded bg-ink-100 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-ink-800">{l.nombreArticulo}</div>
                          <div className="text-xs text-ink-500">
                            {l.cantidad} × {formatUSD(l.precioUnitario)}
                          </div>
                          {l.observaciones && (
                            <div className="text-xs text-amber-700 mt-0.5 italic">📝 {l.observaciones}</div>
                          )}
                        </div>
                        <div className="text-sm font-mono font-semibold text-ink-800">
                          {formatUSD(l.subtotal)}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totales */}
                  <div className="mt-3 space-y-1 text-sm">
                    <div className="flex justify-between text-ink-600">
                      <span>Subtotal</span><span>{formatUSD(p.subtotal)}</span>
                    </div>
                    {Number(p.costoEnvio) > 0 && (
                      <div className="flex justify-between text-ink-600">
                        <span>Envío</span><span>{formatUSD(p.costoEnvio)}</span>
                      </div>
                    )}
                    {Number(p.descuento) > 0 && (
                      <div className="flex justify-between text-emerald-600">
                        <span>Descuento</span><span>-{formatUSD(p.descuento)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-ink-800 pt-2 border-t border-ink-200 text-base">
                      <span>Total</span><span>{formatUSD(p.total)}</span>
                    </div>
                  </div>
                </section>

                {/* Notas internas */}
                <section>
                  <h3 className="text-xs font-bold text-ink-500 uppercase mb-2">Notas internas</h3>
                  <textarea value={notas} onChange={(e) => setNotas(e.target.value)}
                    rows={2} placeholder="Notas para el equipo (no visible al cliente)"
                    className="input w-full text-sm" />
                  <button onClick={() => notasMut.mutate()} disabled={notasMut.isPending}
                    className="text-xs mt-1 px-3 py-1 rounded bg-ink-100 hover:bg-ink-200 inline-flex items-center gap-1">
                    {notasMut.isPending ? <Loader2 className="size-3 animate-spin" /> : <Save className="size-3" />}
                    Guardar
                  </button>
                </section>

                {/* Historial */}
                {p.logs && p.logs.length > 0 && (
                  <section>
                    <h3 className="text-xs font-bold text-ink-500 uppercase mb-2 flex items-center gap-1">
                      <History className="size-3" /> Historial
                    </h3>
                    <div className="text-xs space-y-1">
                      {p.logs.map((log) => (
                        <div key={log.id} className="flex items-center gap-2 text-ink-600">
                          <ChevronRight className="size-3 text-brand-500 shrink-0" />
                          <span className="font-medium">{log.estadoAnterior || "—"} → {log.estadoNuevo}</span>
                          <span className="text-ink-400">·</span>
                          <span>{formatDateTime(log.createdAt)}</span>
                          {log.actorEmail && (
                            <>
                              <span className="text-ink-400">·</span>
                              <span className="text-ink-500">{log.actorEmail}</span>
                            </>
                          )}
                          {log.nota && <span className="text-ink-400 italic">— {log.nota}</span>}
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer: acciones */}
        {p && p.estado !== "entregado" && p.estado !== "cancelado" && (
          <div className="px-5 py-3 border-t border-ink-200 bg-ink-50 flex items-center justify-between gap-2">
            <button onClick={() => setShowCancelar(true)}
              className="px-3 py-2 rounded-lg text-red-700 hover:bg-red-50 text-sm font-medium inline-flex items-center gap-2">
              <XCircle className="size-4" /> Cancelar pedido
            </button>
            <div className="flex gap-2">
              {ESTADOS.filter(e => e.key !== p.estado && e.key !== "nuevo").map(e => (
                <button key={e.key} onClick={() => cambiarMut.mutate(e.key)} disabled={cambiarMut.isPending}
                  className="px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium inline-flex items-center gap-1 disabled:opacity-50">
                  {e.icon}{e.label}
                </button>
              )).slice(0, 3)}
            </div>
          </div>
        )}

        {/* Confirmación cancelar */}
        {showCancelar && (
          <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl p-5 max-w-md w-full">
              <h3 className="font-bold mb-2">Cancelar pedido</h3>
              <p className="text-sm text-ink-600 mb-3">¿Por qué se cancela?</p>
              <textarea value={motivoCancelar} onChange={(e) => setMotivoCancelar(e.target.value)}
                rows={3} placeholder="Motivo de cancelación"
                className="input w-full mb-3" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowCancelar(false)} className="btn-secondary">Volver</button>
                <button onClick={() => cancelarMut.mutate()} disabled={cancelarMut.isPending || !motivoCancelar.trim()}
                  className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50">
                  {cancelarMut.isPending ? "Cancelando…" : "Sí, cancelar pedido"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function PagoBadge({ estado }: { estado: EstadoPagoPedido }) {
  const map = {
    pendiente: { color: "bg-ink-100 text-ink-600", label: "Pendiente" },
    reportado: { color: "bg-amber-100 text-amber-700", label: "Reportado" },
    confirmado: { color: "bg-emerald-100 text-emerald-700", label: "Confirmado ✓" },
    rechazado: { color: "bg-red-100 text-red-700", label: "Rechazado ✗" },
  }[estado];
  return (
    <span className={`text-xs font-medium px-2 py-1 rounded ${map.color}`}>{map.label}</span>
  );
}
